import { keyframes, styled } from '@mui/system';

const flow = keyframes`
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: -1; }
`;

const AnimatedPath = styled('path')({
  animation: `${flow} 1.5s linear infinite`,
});

const INFINITY_PATH = 'M40 20C28 2 4 2 4 20C4 38 28 38 40 20C52 2 76 2 76 20C76 38 52 38 40 20Z';

const GRADIENT_START = '#703594';
const GRADIENT_END = '#44b7e6';

export type InfinitySpinnerSize = 'page' | 'medium' | 'default' | 'inline';

export type InfinitySpinnerProps = {
  size?: InfinitySpinnerSize | number;
  color?: 'brand' | 'inherit';
};

const sizeConfig: Record<InfinitySpinnerSize, { width: number; strokeWidth: number }> = {
  page: { width: 80, strokeWidth: 3.5 },
  medium: { width: 40, strokeWidth: 5 },
  default: { width: 24, strokeWidth: 7 },
  inline: { width: 20, strokeWidth: 8 },
};

export function InfinitySpinner({ size = 'default', color = 'brand' }: InfinitySpinnerProps) {
  const config =
    typeof size === 'number' ? { width: size, strokeWidth: Math.max(3, Math.min(10, 200 / size)) } : sizeConfig[size];

  const useGradient = color === 'brand';
  const strokeColor = useGradient ? 'url(#sgs-infinity-gradient)' : 'currentColor';

  return (
    <svg viewBox="0 0 80 40" width={config.width} height={config.width / 2} fill="none" aria-hidden="true">
      {useGradient && (
        <defs>
          <linearGradient id="sgs-infinity-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={GRADIENT_START} />
            <stop offset="100%" stopColor={GRADIENT_END} />
          </linearGradient>
        </defs>
      )}
      <path
        d={INFINITY_PATH}
        stroke={strokeColor}
        strokeWidth={config.strokeWidth}
        strokeLinecap="round"
        opacity={0.15}
        pathLength={1}
      />
      <AnimatedPath
        d={INFINITY_PATH}
        stroke={strokeColor}
        strokeWidth={config.strokeWidth}
        strokeLinecap="round"
        pathLength={1}
        strokeDasharray="0.15 0.85"
      />
    </svg>
  );
}
